import { useRef, useCallback, useEffect } from "react";

export function useCustomScrollbar(onOverflowChange, onScrollPositionChange) {
  const containerRef = useRef(null);
  const thumbRef = useRef(null);
  const trackRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startScrollRef = useRef(0);
  const animationRef = useRef(null);

  const updateThumbPosition = useCallback(() => {
    if (!containerRef.current || !thumbRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const trackHeight = clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - thumbHeight);

    thumbRef.current.style.height = `${thumbHeight}px`;
    thumbRef.current.style.transform = `translateY(${thumbTop}px)`;

    // Mostrar/ocultar track según overflow
    const hasOverflow = scrollHeight > clientHeight;
    if (thumbRef.current.parentElement) {
      thumbRef.current.parentElement.style.opacity = hasOverflow ? "1" : "0";
    }
    
    // Notificar cambio de overflow
    if (onOverflowChange) {
      onOverflowChange(hasOverflow);
    }

    // Detectar si está en el final (con tolerancia de 5px)
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 5;
    if (onScrollPositionChange) {
      onScrollPositionChange(isAtBottom);
    }
  }, [onOverflowChange, onScrollPositionChange]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    startYRef.current = e.clientY;
    startScrollRef.current = containerRef.current?.scrollTop || 0;
  }, []);

  const smoothScrollTo = useCallback((targetScroll, duration = 400) => {
    if (!containerRef.current) return;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startScroll = containerRef.current.scrollTop;
    const difference = targetScroll - startScroll;
    const startTime = Date.now();

    const easeInOutQuad = (t) => {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    };

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutQuad(progress);

      if (containerRef.current) {
        containerRef.current.scrollTop = startScroll + difference * easedProgress;
      }

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  const handleTrackClick = useCallback((e) => {
    if (!trackRef.current || !containerRef.current || !thumbRef.current) return;
    
    // No hacer nada si el click fue en el thumb
    if (e.target === thumbRef.current || thumbRef.current.contains(e.target)) return;

    const track = trackRef.current;
    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    
    const { scrollHeight, clientHeight } = containerRef.current;
    const trackHeight = clientHeight;
    const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
    
    // Calcular la nueva posición de scroll
    const clickRatio = clickY / trackHeight;
    const newScrollTop = clickRatio * (scrollHeight - clientHeight);
    const targetScroll = Math.max(0, Math.min(scrollHeight - clientHeight, newScrollTop));
    
    smoothScrollTo(targetScroll);
  }, [smoothScrollTo]);

  const scrollToBottom = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollHeight, clientHeight } = containerRef.current;
    const targetScroll = scrollHeight - clientHeight;
    smoothScrollTo(targetScroll);
  }, [smoothScrollTo]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const { scrollHeight, clientHeight } = containerRef.current;
      const trackHeight = clientHeight;
      const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      const maxThumbTop = trackHeight - thumbHeight;

      const deltaY = e.clientY - startYRef.current;
      const scrollRatio = (scrollHeight - clientHeight) / maxThumbTop;
      const newScrollTop = Math.max(
        0,
        Math.min(scrollHeight - clientHeight, startScrollRef.current + deltaY * scrollRatio)
      );

      containerRef.current.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    updateThumbPosition();

    const handleScroll = () => updateThumbPosition();
    const resizeObserver = new ResizeObserver(() => updateThumbPosition());

    container.addEventListener("scroll", handleScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [updateThumbPosition]);

  return { containerRef, thumbRef, trackRef, handleMouseDown, handleTrackClick, scrollToBottom };
}
